import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { ParkingOption, TaxonomyParking, TaxonomyParkingAttribute } from '@/types/api';

interface ParkingSelectorProps {
  value: ParkingOption[];
  onChange: (parking: ParkingOption[]) => void;
  parkingTypes: TaxonomyParking[];
  parkingAttributes: TaxonomyParkingAttribute[];
}

export function ParkingSelector({
  value,
  onChange,
  parkingTypes,
  parkingAttributes,
}: ParkingSelectorProps): JSX.Element {
  // Get selectable attributes for a specific parking type
  // Only checkbox-type attributes (Free, Paid) are meaningful as toggleable options
  // Integer/textarea attributes (Height, How much does it cost?, Additional_information)
  // would require value input which isn't supported in the Bridge UI
  const getAttributesForType = (parkingId: string) => {
    return parkingAttributes.filter(
      (attr) => attr.parkingId === parkingId && attr.inputType === 'checkbox'
    );
  };

  // Add a new parking option
  const handleAddParking = () => {
    onChange([...value, { parkingId: '', parkingAttributeIds: [] }]);
  };

  // Remove a parking option
  const handleRemoveParking = (index: number) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  };

  // Update parking type
  const handleTypeChange = (index: number, parkingId: string) => {
    const newValue = [...value];
    newValue[index] = {
      parkingId,
      parkingAttributeIds: [], // Reset attributes when type changes
    };
    onChange(newValue);
  };

  // Update parking pricing (single selection - Free or Paid, mutually exclusive)
  const handlePricingChange = (index: number, attributeId: string | null) => {
    const newValue = [...value];
    const option = newValue[index];
    if (option) {
      newValue[index] = {
        ...option,
        parkingAttributeIds: attributeId ? [attributeId] : [],
      };
      onChange(newValue);
    }
  };

  // Get available parking types (exclude already selected ones)
  const getAvailableTypes = (currentIndex: number) => {
    const selectedIds = value
      .filter((_, idx) => idx !== currentIndex)
      .map((p) => p.parkingId);
    return parkingTypes.filter((t) => !selectedIds.includes(t.id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Parking Options</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddParking}
          disabled={value.length >= parkingTypes.length}
          data-testid="add-parking-btn"
        >
          <Plus className="mr-1 h-4 w-4" />
          Add Parking
        </Button>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No parking options selected. Click &quot;Add Parking&quot; to add one.
        </p>
      )}

      {value.map((parking, index) => {
        const availableTypes = getAvailableTypes(index);
        const availableAttributes = parking.parkingId
          ? getAttributesForType(parking.parkingId)
          : [];
        const selectedType = parkingTypes.find((t) => t.id === parking.parkingId);

        return (
          <div
            key={`parking-${String(index)}`}
            className="rounded-lg border p-4 space-y-3"
            data-testid={`parking-option-${String(index)}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Parking {String(index + 1)}
                {selectedType ? `: ${selectedType.name}` : ''}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { handleRemoveParking(index); }}
                data-testid={`remove-parking-${String(index)}-btn`}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Type</Label>
              <Select
                value={parking.parkingId || 'none'}
                onValueChange={(val) => { handleTypeChange(index, val === 'none' ? '' : val); }}
              >
                <SelectTrigger data-testid={`parking-type-${String(index)}-select`}>
                  <SelectValue placeholder="Select parking type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select type...</SelectItem>
                  {availableTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {availableAttributes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Pricing</Label>
                <div
                  className="flex gap-4"
                  role="radiogroup"
                  aria-label="Parking pricing"
                  data-testid={`parking-pricing-${String(index)}-group`}
                >
                  {availableAttributes.map((attr) => {
                    const isSelected = parking.parkingAttributeIds?.[0] === attr.id;
                    const radioId = `parking-${String(index)}-pricing-${attr.id}`;
                    return (
                      <label
                        key={attr.id}
                        htmlFor={radioId}
                        className={cn(
                          'flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm transition-colors',
                          isSelected
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-input hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <input
                          type="radio"
                          id={radioId}
                          name={`parking-${String(index)}-pricing`}
                          value={attr.id}
                          checked={isSelected}
                          onChange={() => { handlePricingChange(index, attr.id); }}
                          className="sr-only"
                          data-testid={`parking-pricing-${String(index)}-${attr.name.toLowerCase()}`}
                        />
                        <span
                          className={cn(
                            'h-4 w-4 rounded-full border flex items-center justify-center',
                            isSelected ? 'border-primary' : 'border-muted-foreground'
                          )}
                        >
                          {isSelected && (
                            <span className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </span>
                        {attr.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
